import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../server/firebase';
import carga from '../../carga/carga';
import * as XLSX from 'xlsx';
import './inscripciones.css';

const requiredFields = [
  { key: 'numeroDocumento', label: 'Número de Documento' },
  { key: 'programaInteres', label: 'Programa de Interés' },
  { key: 'numeroContacto', label: 'Número de Contacto' },
  { key: 'primerApellido', label: 'Primer Apellido' },
  { key: 'primerNombre', label: 'Primer Nombre' },
  { key: 'correo', label: 'Correo' },
  { key: 'departamento', label: 'Departamento' },
  { key: 'municipio', label: 'Municipio' },
];

const Inscripciones = ({ onClose }) => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSolicitudes = async () => {
    setLoading(true);
    setError('');

    try {
      const snapshot = await getDocs(collection(db, 'INSCRIPCIONES'));
      const items = [];

      snapshot.forEach((docSnap) => {
        const docId = docSnap.id;
        const data = docSnap.data() || {};

        Object.entries(data).forEach(([requestId, requestData]) => {
          if (!requestData || typeof requestData !== 'object') return;
          if (requestData.desactivada) return;

          items.push({
            id: `${docId}-${requestId}`,
            docId,
            requestId,
            ...requestData,
          });
        });
      });

      // Ordenar por fecha descendente (más nuevo primero)
      items.sort((a, b) => new Date(b.fecha || b.timestamp || 0) - new Date(a.fecha || a.timestamp || 0));

      setSolicitudes(items);
      if (items.length > 0) {
        setSelectedId((prev) => prev || items[0].id);
      } else {
        setSelectedId(null);
      }
    } catch (err) {
      console.error('Error cargando inscripciones:', err);
      setError('No se pudieron cargar las inscripciones. Revisa la consola.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSolicitudes();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelectedData(null);
      return;
    }
    const found = solicitudes.find((item) => item.id === selectedId);
    setSelectedData(found || null);
  }, [selectedId, solicitudes]);

  const getSolicitudFullName = (solicitud) => {
    const nameParts = [
      solicitud.primerNombre,
      solicitud.segundoNombre,
      solicitud.primerApellido,
      solicitud.segundoApellido,
    ].filter(Boolean);

    return nameParts.length > 0 ? nameParts.join(' ') : (solicitud.nombre || '');
  };

  const handleFieldChange = (field, value) => {
    setSelectedData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const acceptSolicitud = async () => {
    if (!selectedData) return;

    const missing = requiredFields
      .filter((field) => !selectedData[field.key] || String(selectedData[field.key]).trim() === '')
      .map((field) => field.label);

    if (missing.length > 0) {
      setError(`Completa los siguientes campos: ${missing.join(', ')}`);
      return;
    }

    try {
      carga.mostrarCarga();
      setError('');

      const studentData = {
        programa: String(selectedData.programaInteres || '').toUpperCase(),
        tipoDocumento: selectedData.tipoDocumento || '',
        documento: String(selectedData.numeroDocumento || '').toUpperCase(),
        primerApellido: String(selectedData.primerApellido || '').toUpperCase(),
        segundoApellido: String(selectedData.segundoApellido || '').toUpperCase(),
        primerNombre: String(selectedData.primerNombre || '').toUpperCase(),
        segundoNombre: String(selectedData.segundoNombre || '').toUpperCase(),
        telefono: selectedData.numeroContacto || '',
        correo: String(selectedData.correo || '').toLowerCase(),
        municipio: selectedData.municipio || '',
        departamento: selectedData.departamento || '',
        grado: selectedData.grado || '',
        icfes: selectedData.icfes || 'NO',
        derechoDeGrado: selectedData.derechoDeGrado || 'NO',
        cuotaPendiente: selectedData.cuotaPendiente !== undefined ? selectedData.cuotaPendiente : null,
      };

      const estudianteRef = doc(db, 'ESTUDIANTES', studentData.documento);
      await setDoc(estudianteRef, studentData, { merge: true });

      const inscripcionRef = doc(db, 'INSCRIPCIONES', selectedData.docId);
      const cleanedRequestData = { ...selectedData };
      delete cleanedRequestData.id;
      delete cleanedRequestData.docId;
      delete cleanedRequestData.requestId;
      cleanedRequestData.desactivada = true;

      await setDoc(inscripcionRef, { [selectedData.requestId]: cleanedRequestData }, { merge: true });

      await loadSolicitudes();
    } catch (err) {
      console.error('Error aceptando solicitud:', err);
      setError('No se pudo aceptar la solicitud. Intenta de nuevo.');
    } finally {
      carga.ocultarCarga();
    }
  };

  const handleDownloadSolicitudes = () => {
    if (solicitudes.length === 0) {
      alert('No hay solicitudes pendientes para descargar.');
      return;
    }

    const data = solicitudes.map((solicitud) => ({
      'ID Solicitud': solicitud.requestId || '',
      'Nombre Completo': getSolicitudFullName(solicitud),
      'Documento': solicitud.numeroDocumento || '',
      'Tipo de Documento': solicitud.tipoDocumento || '',
      'Primer Apellido': solicitud.primerApellido || '',
      'Segundo Apellido': solicitud.segundoApellido || '',
      'Primer Nombre': solicitud.primerNombre || '',
      'Segundo Nombre': solicitud.segundoNombre || '',
      'Correo': solicitud.correo || '',
      'Programa de Interés': solicitud.programaInteres || '',
      'Departamento': solicitud.departamento || '',
      'Municipio': solicitud.municipio || '',
      'Grado': solicitud.grado || '',
      'ICFES': solicitud.icfes || '',
      'Derecho de Grado': solicitud.derechoDeGrado || '',
      'Cuota Pendiente': solicitud.cuotaPendiente !== undefined ? solicitud.cuotaPendiente : '',
      'Fecha': solicitud.fecha ? new Date(solicitud.fecha).toLocaleString('es-CO') : solicitud.timestamp ? new Date(solicitud.timestamp).toLocaleString('es-CO') : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Solicitudes');
    XLSX.writeFile(workbook, 'solicitudes_pendientes.xlsx');
  };

  const selectSolicitud = (id) => {
    setSelectedId(id);
  };

  return (
    <div className="inscripciones-overlay">
      <div className="inscripciones-modal">
        <div className="inscripciones-sidebar">
          <div>
            <h2>Inscripciones</h2>
            <p className="inscripciones-meta">Solicitudes pendientes: {solicitudes.length}</p>
          </div>

          <button className="inscripciones-export" type="button" onClick={handleDownloadSolicitudes}>
            Descargar interesados
          </button>

          <div className="inscripciones-list">
            {loading && <p className="inscripciones-empty">Cargando inscripciones...</p>}
            {!loading && solicitudes.length === 0 && <p className="inscripciones-empty">No hay inscripciones pendientes.</p>}
            {!loading && solicitudes.map((solicitud) => (
              <div
                key={solicitud.id}
                className={`inscripcion-item ${solicitud.id === selectedId ? 'active' : ''}`}
                onClick={() => selectSolicitud(solicitud.id)}
              >
                <strong>{
                  [
                    solicitud.primerNombre,
                    solicitud.segundoNombre,
                    solicitud.primerApellido,
                    solicitud.segundoApellido,
                  ]
                    .filter(Boolean)
                    .join(' ') || solicitud.nombre || solicitud.documento || solicitud.requestId
                }</strong>
                <small>{solicitud.docId}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="inscripciones-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
            <div>
              <h2>Detalles de inscripción</h2>
              <p className="inscripciones-meta">Selecciona una inscripción para completar los datos.</p>
            </div>
            <button className="inscripciones-close" type="button" onClick={() => onClose ? onClose() : null}>
              Cerrar
            </button>
          </div>

          {error && <div className="inscripciones-error">{error}</div>}

          {!selectedData && !loading && <p className="inscripciones-empty">Selecciona una solicitud en la lista para ver los datos.</p>}

          {selectedData && (
            <>
              <div className="inscripciones-fields">
                <div className="inscripciones-field">
                  <label>Número de Documento</label>
                  <input type="text" value={selectedData.numeroDocumento || ''} onChange={(e) => handleFieldChange('numeroDocumento', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Correo</label>
                  <input type="email" value={selectedData.correo || ''} onChange={(e) => handleFieldChange('correo', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Programa de Interés</label>
                  <input type="text" value={selectedData.programaInteres || ''} onChange={(e) => handleFieldChange('programaInteres', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Tipo de Documento</label>
                  <select value={selectedData.tipoDocumento || ''} onChange={(e) => handleFieldChange('tipoDocumento', e.target.value)}>
                    <option value="">Seleccionar</option>
                    <option>Tarjeta de Identidad</option>
                    <option>Cédula de Ciudadanía</option>
                  </select>
                </div>
                <div className="inscripciones-field">
                  <label>Primer Apellido</label>
                  <input type="text" value={selectedData.primerApellido || ''} onChange={(e) => handleFieldChange('primerApellido', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Segundo Apellido</label>
                  <input type="text" value={selectedData.segundoApellido || ''} onChange={(e) => handleFieldChange('segundoApellido', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Primer Nombre</label>
                  <input type="text" value={selectedData.primerNombre || ''} onChange={(e) => handleFieldChange('primerNombre', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Segundo Nombre</label>
                  <input type="text" value={selectedData.segundoNombre || ''} onChange={(e) => handleFieldChange('segundoNombre', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Número de Contacto</label>
                  <input type="text" value={selectedData.numeroContacto || ''} onChange={(e) => handleFieldChange('numeroContacto', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Departamento</label>
                  <input type="text" value={selectedData.departamento || ''} onChange={(e) => handleFieldChange('departamento', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Municipio</label>
                  <input type="text" value={selectedData.municipio || ''} onChange={(e) => handleFieldChange('municipio', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>Grado</label>
                  <input type="text" value={selectedData.grado || ''} onChange={(e) => handleFieldChange('grado', e.target.value)} />
                </div>
                <div className="inscripciones-field">
                  <label>ICFES</label>
                  <select value={selectedData.icfes || 'NO'} onChange={(e) => handleFieldChange('icfes', e.target.value)}>
                    <option>SÍ</option>
                    <option>NO</option>
                  </select>
                </div>
                <div className="inscripciones-field">
                  <label>Derecho de Grado</label>
                  <select value={selectedData.derechoDeGrado || 'NO'} onChange={(e) => handleFieldChange('derechoDeGrado', e.target.value)}>
                    <option>SI</option>
                    <option>NO</option>
                  </select>
                </div>
                <div className="inscripciones-field">
                  <label>Cuota Pendiente</label>
                  <input type="number" value={selectedData.cuotaPendiente || ''} onChange={(e) => handleFieldChange('cuotaPendiente', e.target.value)} />
                </div>
              </div>

              <div className="inscripciones-actions">
                <button className="inscripciones-accept" type="button" onClick={acceptSolicitud}>
                  Aceptar y agregar a Estudiantes
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inscripciones;
